import { useState } from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

import { LLM_COLORS } from "../utils/llmColors";

const PromptRow = ({ pc }) => {
  const [expanded, setExpanded] = useState(false);
  const isValidation = pc.prompt_type === "validation";

  return (
    <div className="bg-surface-container-low rounded-xl border border-surface-container-high overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-surface-container-high/50 transition-colors"
      >
        <span className="mt-0.5 text-on-surface-variant shrink-0">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-on-surface truncate">{pc.prompt_text}</p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          {pc.engine_results.map((er) => {
            const colors = LLM_COLORS[er.engine] || {
              bg: "bg-surface-container-high",
              text: "text-on-surface-variant",
              border: "border-outline-variant/30",
              label: er.engine,
            };
            return (
              <span
                key={er.engine}
                className={`text-xs px-2 py-0.5 rounded-full border font-bold ${colors.bg} ${colors.text} ${colors.border}`}
              >
                {er.mentioned ? (isValidation ? "✓" : `#${er.rank}`) : "—"}
              </span>
            );
          })}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-surface-container-high p-4 space-y-3">
          {pc.engine_results.map((er) => {
            const colors = LLM_COLORS[er.engine] || {
              bg: "bg-surface-container-high",
              text: "text-on-surface-variant",
              border: "border-outline-variant/30",
              label: er.engine,
            };
            return (
              <div key={er.engine} className="space-y-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded-full border ${colors.bg} ${colors.text} ${colors.border}`}
                  >
                    {colors.label}
                  </span>
                  {er.mentioned ? (
                    <span className="text-xs text-emerald-600 font-medium">
                      {isValidation ? "Responded" : `Mentioned — rank #${er.rank}`}
                    </span>
                  ) : (
                    <span className="text-xs text-on-surface-variant">
                      {isValidation ? "No response" : "Not mentioned"}
                    </span>
                  )}
                  {er.model_version && (
                    <span className="text-xs text-on-surface-variant/50 ml-auto">
                      {er.model_version}
                    </span>
                  )}
                </div>
                {!isValidation &&
                  (er.context ? (
                    <p className="text-xs text-on-surface-variant italic pl-2 border-l-2 border-surface-container-high">
                      {er.context}
                    </p>
                  ) : (
                    <p className="text-xs text-on-surface-variant/40">
                      No response excerpt captured
                    </p>
                  ))}
              </div>
            );
          })}
          {isValidation && (
            <p className="text-xs text-on-surface-variant font-medium pt-1">
              Sentiment analysis for this response is available in the Fit Assessment section on the
              Overview tab.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const PersonaPromptExplorer = ({ promptCoverage }) => {
  const discovery = (promptCoverage ?? []).filter((p) => p.prompt_type !== "validation");
  const validation = (promptCoverage ?? []).filter((p) => p.prompt_type === "validation");

  return (
    <div className="space-y-4">
      {discovery.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-on-surface-variant mb-2">
            Discovery Prompts ({discovery.length})
          </h4>
          <div className="space-y-2">
            {discovery.map((pc) => (
              <PromptRow key={pc.prompt_id} pc={pc} />
            ))}
          </div>
        </div>
      )}

      {validation.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-on-surface-variant mb-2">
            Validation Prompts ({validation.length})
          </h4>
          <div className="space-y-2">
            {validation.map((pc) => (
              <PromptRow key={pc.prompt_id} pc={pc} />
            ))}
          </div>
        </div>
      )}

      {!promptCoverage?.length && (
        <p className="text-sm text-on-surface-variant text-center py-4">
          No prompt data available.
        </p>
      )}
    </div>
  );
};

export default PersonaPromptExplorer;
